// Plugin para Google AdSense
class AdSensePlugin {
  constructor() {
    this.isLoaded = false
    this.isLoading = false
    this.adSlots = new Map()
  }

  // Cargar el script de AdSense
  loadAdSense(clientId) {
    if (this.isLoaded || this.isLoading) {
      return Promise.resolve()
    }

    this.isLoading = true

    return new Promise((resolve) => {
      // Verificar si ya existe el script (puede estar en el HTML)
      const existingScript = document.querySelector('script[src*="pagead2.googlesyndication.com"]')
      if (existingScript) {
        // Esperar a que el script se cargue si ya está en el DOM
        if (typeof window.adsbygoogle !== 'undefined') {
          this.isLoaded = true
          this.isLoading = false
          resolve()
          return
        }
        
        // Esperar a que el script se cargue
        const checkInterval = setInterval(() => {
          if (typeof window.adsbygoogle !== 'undefined') {
            clearInterval(checkInterval)
            this.isLoaded = true
            this.isLoading = false
            resolve()
          }
        }, 100)
        
        // Timeout después de 10 segundos (aumentado para dar más tiempo)
        setTimeout(() => {
          clearInterval(checkInterval)
          if (typeof window.adsbygoogle === 'undefined') {
            // No rechazar, solo marcar como no cargado y continuar
            console.warn('AdSense no se pudo cargar. Esto puede deberse a:')
            console.warn('1. Un bloqueador de anuncios activo')
            console.warn('2. Problemas de red')
            console.warn('3. El script está siendo bloqueado por el navegador')
            this.isLoading = false
            // Resolver de todas formas para que la aplicación continúe
            resolve()
          } else {
            this.isLoaded = true
            this.isLoading = false
          }
        }, 10000)
        return
      }

      // Crear el script de AdSense
      const script = document.createElement('script')
      script.async = true
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`
      script.crossOrigin = 'anonymous'
      
      script.onload = () => {
        // Esperar un poco más para asegurar que adsbygoogle esté disponible
        setTimeout(() => {
          if (typeof window.adsbygoogle !== 'undefined') {
            this.isLoaded = true
            this.isLoading = false
            resolve()
          } else {
            // Si después de cargar el script adsbygoogle no está disponible
            console.warn('El script de AdSense se cargó pero adsbygoogle no está disponible')
            this.isLoading = false
            resolve()
          }
        }, 100)
      }
      
      script.onerror = () => {
        console.warn('Error al cargar el script de Google AdSense. Esto puede deberse a un bloqueador de anuncios.')
        this.isLoading = false
        // Resolver en lugar de rechazar para que la aplicación continúe
        resolve()
      }

      // Timeout de seguridad por si el script nunca se carga
      setTimeout(() => {
        if (this.isLoading) {
          console.warn('Timeout esperando a que AdSense se cargue. Continuando sin AdSense.')
          this.isLoading = false
          resolve()
        }
      }, 10000)

      document.head.appendChild(script)
    })
  }

  // Crear un anuncio
  createAd(adSlotId, adFormat = 'auto', adStyle = 'display:block') {
    // Verificar si adsbygoogle está disponible (puede que el script esté bloqueado)
    if (typeof window.adsbygoogle === 'undefined') {
      console.warn('AdSense no está disponible. Puede que esté bloqueado por un bloqueador de anuncios.')
      return null
    }
    
    if (!this.isLoaded) {
      console.warn('AdSense no está cargado aún')
      return null
    }

    const adElement = document.createElement('ins')
    adElement.className = 'adsbygoogle'
    // Asegurar que el elemento tenga dimensiones desde el principio
    // Usar estilos que AdSense reconoce
    const defaultStyle = 'display:block;width:100%;min-height:90px;text-align:center;'
    adElement.style.cssText = defaultStyle + (adStyle || '')
    adElement.setAttribute('data-ad-client', this.clientId)
    adElement.setAttribute('data-ad-slot', adSlotId)
    adElement.setAttribute('data-ad-format', adFormat)
    adElement.setAttribute('data-full-width-responsive', 'true')
    
    // Asegurar que el elemento sea visible
    adElement.style.visibility = 'visible'
    adElement.style.opacity = '1'

    return adElement
  }

  // Verificar si el contenedor tiene dimensiones visibles
  hasVisibleDimensions(element) {
    if (!element) return false
    
    const rect = element.getBoundingClientRect()
    const style = window.getComputedStyle(element)
    
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0'
    )
  }

  // Esperar a que el contenedor tenga dimensiones
  waitForDimensions(container, maxAttempts = 10, delay = 100) {
    return new Promise((resolve, reject) => {
      let attempts = 0
      
      const checkDimensions = () => {
        attempts++
        
        if (this.hasVisibleDimensions(container)) {
          resolve(true)
          return
        }
        
        if (attempts >= maxAttempts) {
          reject(new Error('El contenedor no tiene dimensiones visibles después de varios intentos'))
          return
        }
        
        setTimeout(checkDimensions, delay)
      }
      
      checkDimensions()
    })
  }

  // Esperar a que un elemento tenga dimensiones usando ResizeObserver
  waitForElementDimensions(element, timeout = 5000) {
    return new Promise((resolve, reject) => {
      if (!element) {
        reject(new Error('Elemento no disponible'))
        return
      }

      // Verificar inmediatamente si ya tiene dimensiones
      if (this.hasVisibleDimensions(element)) {
        resolve(true)
        return
      }

      let resolved = false
      let resizeObserver = null
      let checkInterval = null
      
      const cleanup = () => {
        if (resizeObserver) {
          resizeObserver.disconnect()
          resizeObserver = null
        }
        if (checkInterval) {
          clearInterval(checkInterval)
          checkInterval = null
        }
      }

      const timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true
          cleanup()
          reject(new Error('Timeout esperando dimensiones del elemento'))
        }
      }, timeout)

      // Usar ResizeObserver si está disponible
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const { width, height } = entry.contentRect
            if (width > 0 && height > 0 && !resolved) {
              resolved = true
              clearTimeout(timeoutId)
              cleanup()
              resolve(true)
              return
            }
          }
        })

        resizeObserver.observe(element)
      }

      // Verificar periódicamente como fallback
      let attempts = 0
      const maxAttempts = 50
      checkInterval = setInterval(() => {
        attempts++
        if (this.hasVisibleDimensions(element) && !resolved) {
          resolved = true
          clearTimeout(timeoutId)
          cleanup()
          resolve(true)
        } else if (attempts >= maxAttempts && !resolved) {
          resolved = true
          clearTimeout(timeoutId)
          cleanup()
          reject(new Error('No se pudieron obtener dimensiones después de varios intentos'))
        }
      }, 100)
    })
  }

  // Insertar anuncio en un contenedor
  async insertAd(container, adSlotId, adFormat = 'auto', adStyle = 'display:block') {
    if (!container) {
      console.error('El contenedor no está disponible')
      return null
    }

    // Esperar a que el contenedor tenga dimensiones visibles
    try {
      await this.waitForDimensions(container, 20, 100)
    } catch (error) {
      console.warn('Advertencia al verificar dimensiones del contenedor:', error)
      // Continuar de todas formas, pero con un delay más largo
      await new Promise(resolve => setTimeout(resolve, 300))
    }

    const adElement = this.createAd(adSlotId, adFormat, adStyle)
    if (!adElement) {
      return null
    }

    // Obtener dimensiones del contenedor antes de insertar
    const containerRect = container.getBoundingClientRect()
    
    // Asegurar que el elemento tenga dimensiones basadas en el contenedor
    if (containerRect.width > 0) {
      adElement.style.width = `${containerRect.width}px`
    } else {
      // Si el contenedor no tiene ancho, usar el ancho del offset o un mínimo
      const containerWidth = container.offsetWidth || container.clientWidth || 320
      adElement.style.width = `${containerWidth}px`
    }
    adElement.style.minHeight = '90px'
    
    // Insertar el elemento en el DOM
    container.appendChild(adElement)
    this.adSlots.set(adSlotId, adElement)
    
    // Esperar múltiples frames para asegurar que el DOM esté completamente actualizado
    await new Promise(resolve => requestAnimationFrame(resolve))
    await new Promise(resolve => requestAnimationFrame(resolve))
    
    // Verificar dimensiones después de insertar
    const elementRect = adElement.getBoundingClientRect()
    
    // Si aún no tiene dimensiones, intentar establecerlas explícitamente
    if (elementRect.width === 0) {
      const parentWidth = containerRect.width || container.offsetWidth || container.clientWidth || 320
      adElement.style.width = `${parentWidth}px`
      // Esperar otro frame
      await new Promise(resolve => requestAnimationFrame(resolve))
    }
    
    // Asegurar que adsbygoogle esté disponible
    if (typeof window.adsbygoogle === 'undefined') {
      console.error('adsbygoogle no está disponible')
      return adElement
    }
    
    // Esperar un poco más para asegurar que el elemento esté completamente renderizado
    await new Promise(resolve => setTimeout(resolve, 100))
    
    // Verificar que el elemento esté en el DOM y tenga dimensiones
    if (!adElement.parentNode) {
      console.error('El elemento del anuncio no está en el DOM')
      return adElement
    }
    
    // Renderizar el anuncio - AdSense detectará automáticamente el elemento
    try {
      // Inicializar el array si no existe
      window.adsbygoogle = window.adsbygoogle || []
      
      // Verificar que el elemento no haya sido procesado ya
      if (adElement.dataset.adsbygoogleStatus) {
        console.log('El anuncio ya ha sido procesado, estado:', adElement.dataset.adsbygoogleStatus)
        return adElement
      }
      
      // Verificar si el elemento está en el viewport
      const rect = adElement.getBoundingClientRect()
      const isInViewport = rect.top >= 0 && 
                          rect.left >= 0 && 
                          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && 
                          rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      
      console.log('Renderizando anuncio para slot:', adSlotId)
      console.log('Dimensiones:', rect)
      console.log('En viewport:', isInViewport)
      console.log('Atributos:', {
        'data-ad-client': adElement.getAttribute('data-ad-client'),
        'data-ad-slot': adElement.getAttribute('data-ad-slot'),
        'data-ad-format': adElement.getAttribute('data-ad-format')
      })
      
      // Push vacío - AdSense detectará automáticamente los elementos con clase 'adsbygoogle'
      // que aún no han sido procesados
      window.adsbygoogle.push({})
      
      // Esperar un poco para que AdSense procese el anuncio
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Verificar el estado después de esperar
      const status = adElement.dataset.adsbygoogleStatus
      const hasContent = adElement.children.length > 0 || adElement.innerHTML.trim().length > 0
      
      // Obtener el iframe si existe
      let iframe = adElement.querySelector('iframe')
      
      // Función para verificar y corregir el iframe
      const checkAndFixIframe = () => {
        const currentIframe = adElement.querySelector('iframe')
        if (currentIframe) {
          // Actualizar la variable iframe si se encuentra
          iframe = currentIframe
          
          const iframeRect = currentIframe.getBoundingClientRect()
          const iframeStyle = window.getComputedStyle(currentIframe)
          const containerWidth = containerRect.width || container.offsetWidth || 896
          
          console.log('Iframe encontrado:', {
            dimensiones: iframeRect,
            display: iframeStyle.display,
            visibility: iframeStyle.visibility,
            opacity: iframeStyle.opacity,
            width: iframeStyle.width,
            height: iframeStyle.height,
            containerWidth: containerWidth
          })
          
          // Asegurar que el iframe sea visible y tenga dimensiones
          if (iframeRect.width === 0 || iframeRect.height === 0) {
            console.warn('El iframe tiene dimensiones 0x0, intentando corregir...')
            currentIframe.style.width = `${containerWidth}px`
            currentIframe.style.height = '90px'
            currentIframe.style.minHeight = '90px'
            currentIframe.style.minWidth = '320px'
            currentIframe.style.display = 'block'
            currentIframe.style.visibility = 'visible'
            currentIframe.style.opacity = '1'
            currentIframe.style.border = 'none'
            currentIframe.style.margin = '0'
            currentIframe.style.padding = '0'
            
            // También asegurar que el elemento padre tenga las dimensiones correctas
            adElement.style.width = `${containerWidth}px`
            adElement.style.minHeight = '90px'
            adElement.style.display = 'block'
            
            return true // Se corrigió
          }
        }
        return false // No se corrigió
      }
      
      // Verificar inmediatamente
      checkAndFixIframe()
      
      // También verificar después de un tiempo adicional (el iframe puede cargarse más tarde)
      setTimeout(() => {
        if (checkAndFixIframe()) {
          console.log('Iframe corregido después de esperar')
        }
      }, 1000)
      
      // Usar MutationObserver para detectar cuando se añade el iframe
      if (typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.addedNodes.length > 0) {
              mutation.addedNodes.forEach((node) => {
                if (node.tagName === 'IFRAME' || (node.querySelector && node.querySelector('iframe'))) {
                  setTimeout(() => {
                    checkAndFixIframe()
                  }, 100)
                }
              })
            }
          })
        })
        
        observer.observe(adElement, {
          childList: true,
          subtree: true
        })
        
        // Desconectar después de 5 segundos
        setTimeout(() => {
          observer.disconnect()
        }, 5000)
      }
      
      console.log('Estado del anuncio después de renderizar:', status || 'sin estado')
      console.log('Tiene contenido:', hasContent)
      console.log('Número de hijos:', adElement.children.length)
      console.log('HTML del elemento:', adElement.innerHTML.substring(0, 200))
      
      if (!status && !hasContent) {
        console.warn('El anuncio no se ha procesado. Esto puede ser normal si:')
        console.warn('1. El sitio está en modo desarrollo')
        console.warn('2. No hay anuncios disponibles para este slot')
        console.warn('3. La cuenta de AdSense no está completamente configurada')
      } else if (status === 'done' && hasContent && iframe) {
        const iframeRect = iframe.getBoundingClientRect()
        if (iframeRect.width === 0 || iframeRect.height === 0) {
          console.error('El anuncio está marcado como "done" pero el iframe no tiene dimensiones visibles')
          console.error('Esto puede indicar un problema con el CMP (Consent Management Platform) o bloqueadores de anuncios')
          
          // Intentar forzar las dimensiones del iframe
          const containerWidth = containerRect.width || container.offsetWidth || 896
          iframe.style.width = `${containerWidth}px`
          iframe.style.height = '90px'
          iframe.style.minHeight = '90px'
          iframe.style.display = 'block'
          iframe.style.visibility = 'visible'
          iframe.style.opacity = '1'
          
          console.log('Dimensiones del iframe corregidas manualmente')
        }
      }
      
      // Verificar si hay un CMP activo que pueda estar bloqueando - COMENTADO (iubenda deshabilitado)
      // if (window._iub && window._iub.csConfiguration) {
      //   console.log('CMP (iubenda) detectado. Verificando consentimiento...')
      //   
      //   // Verificar el estado del consentimiento
      //   if (window._iub && window._iub.cs && window._iub.cs.api) {
      //     window._iub.cs.api.getConsent().then((consent) => {
      //       console.log('Estado del consentimiento:', consent)
      //       if (!consent || !consent.consent) {
      //         console.warn('El usuario no ha dado su consentimiento. Los anuncios pueden estar bloqueados.')
      //       }
      //     }).catch((error) => {
      //       console.warn('No se pudo verificar el consentimiento:', error)
      //     })
      //   }
      //   
      //   // Verificar si el iframe tiene contenido real
      //   if (iframe) {
      //     try {
      //       // Intentar acceder al contenido del iframe (puede fallar por CORS)
      //       const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
      //       if (iframeDoc) {
      //         const iframeBody = iframeDoc.body
      //         if (iframeBody) {
      //           const hasVisibleContent = iframeBody.children.length > 0 || 
      //                                    iframeBody.innerHTML.trim().length > 0 ||
      //                                    iframeBody.offsetHeight > 0
      //           console.log('Contenido del iframe:', {
      //             tieneHijos: iframeBody.children.length,
      //             tieneHTML: iframeBody.innerHTML.trim().length > 0,
      //             altura: iframeBody.offsetHeight,
      //             tieneContenidoVisible: hasVisibleContent
      //           })
      //           
      //           if (!hasVisibleContent) {
      //             console.warn('El iframe no tiene contenido visible. Puede estar bloqueado por el CMP.')
      //           }
      //         }
      //       } else {
      //         console.warn('No se puede acceder al contenido del iframe (CORS o bloqueado por CMP)')
      //       }
      //     } catch (error) {
      //       console.warn('Error al verificar el contenido del iframe (normal por CORS):', error.message)
      //     }
      //   }
      // }
      
      // Verificar también los divs de AdSense (aswift_host)
      const aswiftHosts = adElement.querySelectorAll('div[id^="aswift_"]')
      if (aswiftHosts.length > 0) {
        console.log('Divs de AdSense encontrados:', aswiftHosts.length)
        aswiftHosts.forEach((host, index) => {
          const hostRect = host.getBoundingClientRect()
          const hostStyle = window.getComputedStyle(host)
          console.log(`Div aswift_${index}:`, {
            dimensiones: hostRect,
            display: hostStyle.display,
            visibility: hostStyle.visibility,
            opacity: hostStyle.opacity,
            zIndex: hostStyle.zIndex
          })
          
          // Verificar si el div está oculto
          if (hostStyle.display === 'none' || hostStyle.visibility === 'hidden' || hostStyle.opacity === '0') {
            console.warn(`El div aswift_${index} está oculto. Intentando corregir...`)
            host.style.display = 'block'
            host.style.visibility = 'visible'
            host.style.opacity = '1'
          }
        })
      }
    } catch (error) {
      console.error('Error al renderizar anuncio:', error)
    }
    
    return adElement
  }

  // Limpiar anuncios
  clearAds() {
    this.adSlots.forEach((adElement) => {
      if (adElement.parentNode) {
        adElement.parentNode.removeChild(adElement)
      }
    })
    this.adSlots.clear()
  }

  // Verificar si AdSense está disponible
  isAdSenseAvailable() {
    // Verificar si el script ya está cargado (puede estar en el HTML)
    const scriptExists = document.querySelector('script[src*="pagead2.googlesyndication.com"]')
    const adsbygoogleExists = typeof window.adsbygoogle !== 'undefined'
    
    // Si el script existe en el DOM y adsbygoogle está disponible, considerar que está cargado
    if (scriptExists && adsbygoogleExists) {
      this.isLoaded = true
      return true
    }
    
    return this.isLoaded && adsbygoogleExists
  }
}

// Crear instancia global
const adSense = new AdSensePlugin()

// Plugin de Vue
export default {
  install(app, options = {}) {
    const { clientId } = options
    
    if (clientId) {
      adSense.clientId = clientId
      // Cargar AdSense automáticamente
      adSense.loadAdSense(clientId)
    }

    // Proporcionar AdSense globalmente
    app.config.globalProperties.$adSense = adSense
    
    // También disponible en el contexto de la aplicación
    app.provide('adSense', adSense)
  }
}

// Exportar la instancia para uso directo
export { adSense } 